/**
 * Slice F4 — safe authentication logging.
 *
 * Auth code previously logged submitted email addresses and whole user objects
 * (`console.log('...', response.user)`). Anything logged here lands in the browser
 * console, in session-replay/analytics capture, and in screenshots attached to
 * support tickets, so a full user object is a real PII leak rather than a
 * cosmetic one.
 *
 * The rule this module enforces: log SHAPE and OUTCOME, never CONTENT.
 * "a user was returned and it had an id" is a useful diagnostic;
 * "the user is alice@example.com with public_id abc-123" is a leak.
 *
 * Never log, persist, decode, or send: tokens, cookies, passwords, beta keys,
 * `external_user_id`, `__clerk_db_jwt`, email addresses, usernames, `public_id`,
 * or raw vendor responses.
 */

const REDACTED = '[redacted]';

/**
 * Keys whose values must never be logged, matched case-insensitively as
 * substrings so `user_email`, `betaKey`, `clerk_token` etc. are all caught.
 */
const SENSITIVE_KEY_PATTERN = /token|jwt|password|secret|beta_?key|invite|cookie|authorization|email|username|public_?id|external_?user_?id|session|claims|sub$/i;

/**
 * Reduce an unknown value to a non-identifying description.
 *
 * Objects become a key-shape summary with every sensitive value redacted, so a
 * developer can still see "the backend returned an object with id/username/
 * mythic_profile" without the values themselves.
 */
export function redactForLog(value, depth = 0) {
    if (value === null || value === undefined) return value;

    const type = typeof value;
    if (type === 'string') return REDACTED;
    if (type === 'number' || type === 'boolean') return value;
    if (type === 'function') return '[function]';

    if (Array.isArray(value)) {
        if (depth >= 2) return `[array(${value.length})]`;
        return `[array(${value.length})]`;
    }

    if (type === 'object') {
        if (depth >= 2) return '[object]';
        const output = {};
        for (const key of Object.keys(value)) {
            if (SENSITIVE_KEY_PATTERN.test(key)) {
                output[key] = REDACTED;
                continue;
            }
            output[key] = redactForLog(value[key], depth + 1);
        }
        return output;
    }

    return REDACTED;
}

/**
 * Describe a user object for logging without exposing identity.
 *
 * Deliberately does NOT include the user id. The id is a stable identifier that
 * correlates a console/session-replay record back to a specific person, and no
 * auth diagnostic needs it -- knowing whether an id was present is enough.
 */
export function describeUser(user) {
    if (!user) return { present: false };
    return {
        present: true,
        hasId: Boolean(user.id),
        idType: typeof user.id,
        hasProfile: Boolean(user.mythic_profile)
    };
}

/**
 * Describe an error for logging: its type and status, never its payload.
 *
 * `error.message` is intentionally omitted for API errors because backend
 * messages can embed submitted values.
 */
export function describeError(error) {
    if (!error) return { present: false };
    return {
        present: true,
        name: error.name || 'Error',
        status: error.status,
        errorType: error.errorType
    };
}
